import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, InputNumber, List, Space, Tag, Typography, message, theme } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone, WarningTwoTone } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageToolbar from "../../../../design-system/PageToolbar";
import { errorText, openBlob } from "../api";
import * as api from "./api";
import AllowanceSection from "./AllowanceSection";

const YearEndTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getYearEnd(year);
      setData(res.data);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (kind, promise, name) => {
    setExporting(kind);
    try {
      await openBlob(promise, name);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setExporting("");
    }
  };

  const icon = item => {
    if (item.ok)
      return <CheckCircleTwoTone twoToneColor={token.colorSuccess} style={{ fontSize: 20 }} />;
    if (item.soft)
      return <WarningTwoTone twoToneColor={token.colorWarning} style={{ fontSize: 20 }} />;
    return <CloseCircleTwoTone twoToneColor={token.colorError} style={{ fontSize: 20 }} />;
  };

  return (
    <div>
      <PageToolbar
        filters={
          <InputNumber
            addonBefore={t("Year")}
            value={year}
            onChange={v => v && setYear(v)}
            min={2000}
            max={2100}
          />
        }
        actions={
          <div className="acct-actions">
            <Button onClick={load}>{t("Refresh")}</Button>
            <Button
              type="primary"
              loading={exporting === "datev"}
              onClick={() =>
                download("datev", api.downloadDatevPackage({ year }), `DATEV_Export_${year}.zip`)
              }
            >
              {t("Export for tax advisor (DATEV)")}
            </Button>
            <Button
              loading={exporting === "gobd"}
              onClick={() => download("gobd", api.downloadGobd(year), `GoBD_Z3_${year}.zip`)}
            >
              {t("GoBD data export (Z3)")}
            </Button>
          </div>
        }
      />
      {data ? (
        <Alert
          type={data.ready ? "success" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            data.ready
              ? t("All checks passed — the year can go to the tax advisor.")
              : `${data.blocking} ${t("open items block the closing")}, ${data.warnings} ${t("warnings")}`
          }
        />
      ) : null}
      <List
        loading={loading}
        bordered
        dataSource={data?.items || []}
        renderItem={item => (
          <List.Item
            actions={[
              item.link ? (
                <Link key="link" to={item.link}>
                  {t("Open")}
                </Link>
              ) : null
            ]}
          >
            <List.Item.Meta
              avatar={icon(item)}
              title={
                <Space wrap className="acct-wrap">
                  <span>{item.label}</span>
                  {!item.ok && item.count ? (
                    <Tag color={item.soft ? "gold" : "red"}>{item.count}</Tag>
                  ) : null}
                </Space>
              }
              description={
                typeof item.detail === "string"
                  ? item.detail
                  : Array.isArray(item.detail)
                    ? item.detail.map((d, i) => (
                        <div
                          key={i}
                          className="acct-wrap"
                        >{`${d.name || d.account}: ${t("bank")} ${(d.bank / 100).toFixed(2)} € / ${t("ledger")} ${(d.ledger / 100).toFixed(2)} €`}</div>
                      ))
                    : null
              }
            />
          </List.Item>
        )}
      />
      <AllowanceSection year={year} onBooked={load} />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        {t(
          "The DATEV package contains the Buchungsstapel (EXTF, Windows-1252), Debitoren/Kreditoren master data, the Belege index, the cost centre mapping and the SuSa. The GoBD export holds every journal line and the chart of accounts as CSV."
        )}
      </Typography.Paragraph>
    </div>
  );
};

export default YearEndTab;
